/**
 * Декодирование изображений и подготовка к работе.
 *
 * Все изображения конвертируются в data-URL (PNG) для надёжной отрисовки,
 * автосохранения и экспорта (без проблем с CORS/tainted canvas).
 */
import type { ProjectImage } from '../domain/types';

/** Прочитать File/Blob в data URL. */
export function blobToDataURL(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/**
 * Загрузить изображение и получить его размеры.
 * SVG растеризуется: отрисовывается в canvas и сохраняется как PNG.
 */
export async function decodeImage(src: string): Promise<ProjectImage> {
  const img = await loadImage(src);
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;

  // Если это SVG — растеризуем в PNG, чтобы экспорт и hit-test работали корректно.
  if (src.startsWith('data:image/svg') || src.endsWith('.svg')) {
    const png = rasterize(img, w, h);
    return { width: w, height: h, src: png };
  }
  // Для остальных — оставляем исходный data-URL (быстрее, без потерь).
  return { width: w, height: h, src };
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Не удалось загрузить изображение'));
    img.src = src;
  });
}

/** Растеризовать Image в canvas и получить PNG data URL. */
function rasterize(img: HTMLImageElement, w: number, h: number): string {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(img, 0, 0, w, h);
  return canvas.toDataURL('image/png');
}

/** Загрузить HTMLImageElement из ProjectImage.src — для повторного использования. */
export function loadImageElement(src: string): Promise<HTMLImageElement> {
  return loadImage(src);
}

/**
 * Подобрать масштаб «вписать в окно» с сохранением пропорций.
 */
export function fitScale(imgW: number, imgH: number, boxW: number, boxH: number): number {
  return Math.min(boxW / imgW, boxH / imgH, 1);
}
